import {
  MutableRefObject,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { createPortal } from "react-dom";
import { createContext } from "use-context-selector";

interface ModalContextData {
  addComponent: (key: string, component: ReactElement) => void;
  removeComponent: (key: string) => void;
  forceHide: MutableRefObject<any>;
  triggerForceHide: () => void;
}

export const ModalContext = createContext({} as ModalContextData);
export const ModalRootId = "modal-root";

interface ModalProviderProps {
  children: ReactNode;
}

export function ModalProvider({ children }: ModalProviderProps) {
  const [components, setComponents] = useState<{ [key: string]: ReactElement }>({});
  const [modalRootElement, setModalRootElement] = useState<HTMLElement>();
  const [forceHide, setForceHide] = useState<any>();

  useEffect(() => {
    setModalRootElement(document.getElementById(ModalRootId));
    return () => setModalRootElement(null);
  }, []);

  const addComponent = useCallback((key: string, component: ReactElement) => {
    setComponents((old) => ({ ...old, [key]: component }));
  }, []);

  const removeComponent = useCallback((key: string) => {
    setComponents((old) => {
      delete old[key];
      return { ...old };
    });
  }, []);

  const value = useMemo(
    () => ({
      addComponent,
      removeComponent,
      forceHide,
      triggerForceHide: () => {
        document.body.style.overflow = "";
        document.body.style.pointerEvents = "";
        setForceHide({});
      },
    }),
    [addComponent, forceHide, removeComponent]
  );

  return (
    <ModalContext.Provider value={value}>
      {children}
      <div id={ModalRootId}>
        {modalRootElement &&
          Object.entries(components).map(([key, component]) =>
            createPortal(component, modalRootElement, key)
          )}
      </div>
    </ModalContext.Provider>
  );
}
